/**
 * Typed API client. Same-origin in production (Express serves this bundle),
 * Vite-proxied in dev — so no CORS headers exist on the backend and none are
 * needed. Money values pass through as strings, untouched.
 */

import type {
  Destination,
  DestinationKind,
  Direction,
  Order,
  Quote,
  User,
} from './types';

const TOKEN_KEY = 'apexflow.token';

export const tokenStore = {
  get(): string | null {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set(token: string): void {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      /* private mode — session continues in memory only */
    }
  },
  clear(): void {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ignore */
    }
  },
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: { path: string; message: string }[],
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the session is gone and the user must sign in again. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'DELETE';
  body?: unknown;
  idempotencyKey?: string;
  auth?: boolean;
}

/** Session-expiry hook — set once by the auth provider. */
let onUnauthenticated: (() => void) | null = null;
export function setUnauthenticatedHandler(fn: () => void): void {
  onUnauthenticated = fn;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, idempotencyKey, auth = true } = opts;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
  if (auth) {
    const token = tokenStore.get();
    if (token) headers.authorization = `Bearer ${token}`;
  }

  const res = await fetch(path, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const e = (payload as { error?: { code?: string; message?: string; details?: never } })?.error;
    if (res.status === 401 && auth) onUnauthenticated?.();
    throw new ApiError(
      res.status,
      e?.code ?? 'UNKNOWN',
      e?.message ?? `Request failed (${res.status})`,
      e?.details,
    );
  }
  return payload as T;
}

/**
 * Idempotency-Key for order creation. The backend requires >= 16 chars and
 * enforces uniqueness at the DB level, so a replayed submit returns the
 * original order instead of creating a second one.
 */
export function newIdempotencyKey(): string {
  return `web-${crypto.randomUUID()}`;
}

export const api = {
  health: () =>
    request<{ status: string; db: boolean; redis: string; provider: string }>('/health', {
      auth: false,
    }),

  requestOtp: (phone: string) =>
    request<{ sent: true; devHint?: string }>('/v1/auth/otp/request', {
      method: 'POST',
      body: { phone },
      auth: false,
    }),

  verifyOtp: (phone: string, otp: string) =>
    request<{ token: string; user: Pick<User, 'id' | 'phone' | 'kycStatus'> }>(
      '/v1/auth/otp/verify',
      { method: 'POST', body: { phone, otp }, auth: false },
    ),

  me: () => request<User>('/v1/me'),

  startKyc: () => request<{ kycUrl: string; note: string }>('/v1/me/kyc/start', { method: 'POST' }),

  createQuote: (direction: Direction, amount: { fiatAmount?: string; cryptoAmount?: string }) =>
    request<Quote>('/v1/quotes', { method: 'POST', body: { direction, ...amount } }),

  createOrder: (quoteId: string, destinationId: string, idempotencyKey: string) =>
    request<Order>('/v1/orders', {
      method: 'POST',
      body: { quoteId, destinationId },
      idempotencyKey,
    }),

  getOrder: (id: string) => request<Order>(`/v1/orders/${id}`),

  listOrders: (limit = 20, before?: string) =>
    request<{ items: Order[]; nextBefore: string | null }>(
      `/v1/orders?limit=${limit}${before ? `&before=${encodeURIComponent(before)}` : ''}`,
    ),

  listDestinations: () => request<{ items: Destination[] }>('/v1/destinations'),

  addWallet: (address: string, label?: string) =>
    request<Destination>('/v1/destinations', {
      method: 'POST',
      body: { kind: 'CRYPTO_WALLET' as DestinationKind, address, ...(label ? { label } : {}) },
    }),

  addBankAccount: (accountNumber: string, ifsc: string, accountHolderName: string) =>
    request<Destination>('/v1/destinations', {
      method: 'POST',
      body: { kind: 'BANK_ACCOUNT' as DestinationKind, accountNumber, ifsc, accountHolderName },
    }),

  removeDestination: (id: string) =>
    request<void>(`/v1/destinations/${id}`, { method: 'DELETE' }),

};
